/**
 * PDF Generation Utility
 *
 * Generates a checklist PDF from filtered items with:
 * - Section 1: Items Summary (checkbox, name with sub-items/attachments, date, priority, status)
 * - Section 2: Detailed View (checkbox, all item fields with sub-items/attachments)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Item, SubItem, Attachment } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface PdfOptions {
  title: string;
  items: Item[];
  subItemsMap: Record<string, SubItem[]>;
  getCategoryName?: (categoryId: string) => string;
  getOwnerName?: (ownerId: string) => string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Parse date from various formats
 */
function parseDate(d: unknown): Date | null {
  if (!d) return null;
  if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
    return (d as { toDate: () => Date }).toDate();
  }
  if (d instanceof Date) return d;
  if (typeof d === 'object' && d !== null && '_seconds' in d) {
    return new Date((d as { _seconds: number })._seconds * 1000);
  }
  if (typeof d === 'object' && d !== null && 'seconds' in d) {
    return new Date((d as { seconds: number }).seconds * 1000);
  }
  return new Date(d as string | number);
}

/**
 * Format date for display
 */
function formatDate(date: Date | null): string {
  if (!date) return 'None';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Capitalize first letter
 */
function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format priority for display
 */
function formatPriority(priority: string | undefined): string {
  if (!priority) return 'Medium';
  return capitalize(priority);
}

/**
 * Format status for display
 */
function formatStatus(status: string | undefined): string {
  if (!status) return 'New';
  return status.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number | undefined, currency: string = 'USD'): string {
  if (amount === undefined || amount === null) return '';
  const dollars = amount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(dollars);
}

/**
 * Get item type label
 */
function getTypeLabel(type: string): string {
  switch (type) {
    case 'task': return 'Task';
    case 'transaction': return 'Transaction';
    case 'note': return 'Note';
    case 'keyDate': return 'Key Date';
    default: return capitalize(type);
  }
}

/**
 * Get attachments from item
 */
function getAttachments(item: Item | SubItem): Attachment[] {
  return (item as unknown as { attachments?: Attachment[] }).attachments || [];
}

/**
 * Sort children (sub-items and attachments) by order
 */
function getSortedChildren(
  subItems: SubItem[],
  attachments: Attachment[]
): Array<{ type: 'subItem' | 'attachment'; item: SubItem | Attachment }> {
  const children: Array<{ type: 'subItem' | 'attachment'; item: SubItem | Attachment; order: number }> = [];

  subItems.forEach((si) => {
    children.push({ type: 'subItem', item: si, order: (si as unknown as { order?: number }).order ?? 0 });
  });

  attachments.forEach((att) => {
    children.push({ type: 'attachment', item: att, order: att.order ?? 0 });
  });

  children.sort((a, b) => a.order - b.order);
  return children.map(({ type, item }) => ({ type, item }));
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

export function generateItemsPdf(options: PdfOptions): void {
  const { title, items, subItemsMap, getCategoryName, getOwnerName } = options;

  if (items.length === 0) {
    alert('No items to print');
    return;
  }

  // Create PDF (A4 portrait)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const margin = 15;
  let yPos = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, margin, yPos);
  yPos += 8;

  // Subtitle with date and count
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleString()} | ${items.length} item${items.length !== 1 ? 's' : ''}`, margin, yPos);
  yPos += 10;

  // ============================================================================
  // SECTION 1: ITEMS SUMMARY
  // ============================================================================

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Items Summary', margin, yPos);
  yPos += 6;

  // Summary table data - Name column includes sub-items/attachments
  const summaryData = items.map((item) => {
    const dueDate = parseDate((item as unknown as { dueDate?: unknown }).dueDate);
    const createdDate = parseDate((item as unknown as { createdAt?: unknown }).createdAt);
    const displayDate = dueDate || createdDate;
    const status = (item as unknown as { status?: string }).status;
    const attachments = getAttachments(item);
    const subItems = subItemsMap[item.id] || [];
    const children = getSortedChildren(subItems, attachments);

    // Build name cell with nested children
    let nameContent = item.name || 'Untitled';

    if (children.length > 0) {
      nameContent += '\n';
      children.forEach((child) => {
        if (child.type === 'subItem') {
          const si = child.item as SubItem;
          const siDate = parseDate((si as unknown as { dueDate?: unknown }).dueDate);
          nameContent += `    \u2610 ${si.name || 'Untitled'} | ${formatDate(siDate)}\n`;
        } else {
          const att = child.item as Attachment;
          nameContent += `    \u2610 ${att.name}\n`;
        }
      });
    }

    return [
      '', // Checkbox placeholder
      nameContent.trim(),
      formatDate(displayDate),
      formatPriority(item.priority),
      formatStatus(status),
    ];
  });

  // Draw summary table
  autoTable(doc, {
    startY: yPos,
    head: [['', 'Name', 'Date', 'Priority', 'Status']],
    body: summaryData,
    theme: 'plain',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200],
      lineWidth: 0.25,
      overflow: 'linebreak',
    },
    headStyles: {
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 25 },
      3: { cellWidth: 20 },
      4: { cellWidth: 22 },
    },
    didDrawCell: (data) => {
      // Draw checkbox in first column for parent items only
      if (data.column.index === 0 && data.section === 'body') {
        const x = data.cell.x + 2;
        const y = data.cell.y + 4;
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.3);
        doc.rect(x, y, 4, 4);
      }
    },
    margin: { left: margin, right: margin },
  });

  // Get final Y position after summary table
  yPos = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 15;

  // ============================================================================
  // SECTION 2: DETAILED VIEW
  // ============================================================================

  // Check if we need a new page
  if (yPos > doc.internal.pageSize.getHeight() - 50) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Detailed View', margin, yPos);
  yPos += 6;

  // Build detailed data for each item
  const detailedData: string[][] = [];

  items.forEach((item) => {
    const dueDate = parseDate((item as unknown as { dueDate?: unknown }).dueDate);
    const createdDate = parseDate((item as unknown as { createdAt?: unknown }).createdAt);
    const status = (item as unknown as { status?: string }).status;
    const description = (item as unknown as { description?: string }).description;
    const owner = (item as unknown as { owner?: string }).owner;
    const category = (item as unknown as { category?: string }).category;
    const subCategory = (item as unknown as { subCategory?: string }).subCategory;
    const recurrence = (item as unknown as { recurrence?: string }).recurrence;
    const amount = (item as unknown as { amount?: number }).amount;
    const currency = (item as unknown as { currency?: string }).currency;
    const transactionType = (item as unknown as { transactionType?: string }).transactionType;
    const content = (item as unknown as { content?: string }).content;
    const tags = (item as unknown as { tags?: string[] }).tags;
    const urls = (item as unknown as { urls?: { url: string }[] }).urls;
    const attachments = getAttachments(item);
    const subItems = subItemsMap[item.id] || [];
    const children = getSortedChildren(subItems, attachments);

    // Build detail lines
    const lines: string[] = [];

    // Line 1: Name
    lines.push(`Name: ${item.name || 'Untitled'}`);

    // Line 2: Type, Status, Priority
    const shortFields1: string[] = [];
    shortFields1.push(`Type: ${getTypeLabel(item.type)}`);
    shortFields1.push(`Status: ${formatStatus(status)}`);
    shortFields1.push(`Priority: ${formatPriority(item.priority)}`);
    lines.push(shortFields1.join(' \u00B7 '));

    // Line 3: Due Date, Created Date
    const shortFields2: string[] = [];
    if (dueDate) {
      shortFields2.push(`Due: ${formatDate(dueDate)}`);
    }
    if (createdDate) {
      shortFields2.push(`Created: ${formatDate(createdDate)}`);
    }
    if (shortFields2.length > 0) {
      lines.push(shortFields2.join(' \u00B7 '));
    }

    // Line 4: Owner, Category, SubCategory
    const shortFields3: string[] = [];
    if (owner) {
      const ownerName = getOwnerName ? getOwnerName(owner) : owner;
      shortFields3.push(`Owner: ${ownerName}`);
    }
    if (category && category !== 'None') {
      const categoryName = getCategoryName ? getCategoryName(category) : category;
      shortFields3.push(`Category: ${categoryName}`);
    }
    if (subCategory && subCategory !== 'None') {
      shortFields3.push(`SubCategory: ${subCategory}`);
    }
    if (shortFields3.length > 0) {
      lines.push(shortFields3.join(' \u00B7 '));
    }

    // Transaction-specific fields
    if (item.type === 'transaction') {
      const txFields: string[] = [];
      if (transactionType) {
        txFields.push(`Type: ${capitalize(transactionType)}`);
      }
      if (amount !== undefined) {
        txFields.push(`Amount: ${formatCurrency(amount, currency)}`);
      }
      if (txFields.length > 0) {
        lines.push(txFields.join(' \u00B7 '));
      }
    }

    // Recurrence
    if (recurrence && recurrence !== 'none') {
      lines.push(`Recurrence: ${capitalize(recurrence)}`);
    }

    // Tags (for notes)
    if (tags && tags.length > 0) {
      lines.push(`Tags: ${tags.join(', ')}`);
    }

    // URLs
    if (urls && urls.length > 0) {
      const urlStrings = urls.map((u) => typeof u === 'string' ? u : u.url);
      lines.push(`URLs: ${urlStrings.join(', ')}`);
    }

    // Description
    if (description && description.trim()) {
      lines.push(`Description: ${description}`);
    }

    // Content (for notes)
    if (content && content.trim()) {
      lines.push(`Content: ${content}`);
    }

    // Add children (sub-items and attachments)
    if (children.length > 0) {
      lines.push(''); // Blank line before children
      lines.push('--- Sub-Items & Attachments ---');

      children.forEach((child) => {
        if (child.type === 'subItem') {
          const si = child.item as SubItem;
          const siLines = buildSubItemDetailLines(si, getCategoryName, getOwnerName);
          lines.push('');
          lines.push(`  \u2610 ${siLines.join('\n    ')}`);
        } else {
          const att = child.item as Attachment;
          lines.push('');
          lines.push(`  \u2610 Attachment: ${att.name}`);
        }
      });
    }

    detailedData.push(['', lines.join('\n')]);
  });

  // Draw detailed table
  autoTable(doc, {
    startY: yPos,
    head: [['', 'Item Details']],
    body: detailedData,
    theme: 'plain',
    styles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200],
      lineWidth: 0.25,
      overflow: 'linebreak',
    },
    headStyles: {
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 8, valign: 'top' },
      1: { cellWidth: 'auto' },
    },
    didDrawCell: (data) => {
      // Draw checkbox in first column
      if (data.column.index === 0 && data.section === 'body') {
        const x = data.cell.x + 2;
        const y = data.cell.y + 4;
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.3);
        doc.rect(x, y, 4, 4);
      }
    },
    margin: { left: margin, right: margin },
  });

  // Save the PDF
  const filename = `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

/**
 * Build detail lines for a sub-item
 */
function buildSubItemDetailLines(
  si: SubItem,
  getCategoryName?: (id: string) => string,
  getOwnerName?: (id: string) => string
): string[] {
  const lines: string[] = [];
  const anyItem = si as unknown as Record<string, unknown>;

  // Name
  lines.push(`Name: ${si.name || 'Untitled'}`);

  // Type, Status, Priority
  const shortFields1: string[] = [];
  shortFields1.push(`Type: ${getTypeLabel(si.type)}`);
  const status = anyItem.status as string | undefined;
  shortFields1.push(`Status: ${formatStatus(status)}`);
  shortFields1.push(`Priority: ${formatPriority(si.priority)}`);
  lines.push(shortFields1.join(' \u00B7 '));

  // Due Date
  const dueDate = parseDate(anyItem.dueDate);
  if (dueDate) {
    lines.push(`Due: ${formatDate(dueDate)}`);
  }

  // Owner, Category
  const shortFields2: string[] = [];
  if (si.owner) {
    const ownerName = getOwnerName ? getOwnerName(si.owner) : si.owner;
    shortFields2.push(`Owner: ${ownerName}`);
  }
  if (si.category && si.category !== 'None') {
    const categoryName = getCategoryName ? getCategoryName(si.category) : si.category;
    shortFields2.push(`Category: ${categoryName}`);
  }
  if (shortFields2.length > 0) {
    lines.push(shortFields2.join(' \u00B7 '));
  }

  // Description
  const description = anyItem.description as string | undefined;
  if (description && description.trim()) {
    lines.push(`Description: ${description}`);
  }

  // Transaction-specific
  if (si.type === 'transaction') {
    const txFields: string[] = [];
    const transactionType = anyItem.transactionType as string | undefined;
    const amount = anyItem.amount as number | undefined;
    const currency = anyItem.currency as string | undefined;
    if (transactionType) {
      txFields.push(`Type: ${capitalize(transactionType)}`);
    }
    if (amount !== undefined) {
      txFields.push(`Amount: ${formatCurrency(amount, currency)}`);
    }
    if (txFields.length > 0) {
      lines.push(txFields.join(' \u00B7 '));
    }
  }

  // Note-specific
  if (si.type === 'note') {
    const content = anyItem.content as string | undefined;
    if (content && content.trim()) {
      lines.push(`Content: ${content}`);
    }
  }

  // Sub-item attachments
  const attachments = (anyItem.attachments as Attachment[]) || [];
  if (attachments.length > 0) {
    lines.push(`Attachments: ${attachments.map((a) => a.name).join(', ')}`);
  }

  return lines;
}
